import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ChevronsDownUp, ChevronsUpDown, FileText, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, HeaderRow, LoadingState, rowLink, TableShell, TH } from "@/components/PageUi";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { BooksPeriodScreen, DifferenceCell, drCr, money, moneyOrBlank, num } from "./shared";
import { financialYearStart, monthLabel, monthOf, monthsBetween, periodLabel, todayIso, useMonthlySummary, usePeriodLink, type Period } from "./period";
import { formatCalendarDate } from "@/lib/format";
import { InfoHint } from "@/components/InfoHint";
import {
  ExportButton,
  MAX_MONTHS,
  MONTH_TABLE,
  PINNED_CELL,
  csvName,
  downloadCsv,
  useScrollToEnd,
  MonthBars,
  SOLID_ROW,
  STICKY_CELL,
  ViewToggle,
  buckets,
  drillKey,
  readAll,
  useDrilldown,
  usePlatform,
  platformName,
  type DrillTarget,
  type Platform,
  type ReportView,
} from "./ReportKit";
import type { BalanceSheet, ProfitAndLoss, StatementGroup } from "./types";

const CLASSIFICATION_LABEL: Record<string, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  revenue: "Revenue",
  expense: "Expenses",
  // Profit and loss sections.
  Revenue: "Revenue",
  CostOfSales: "Cost of sales",
  OperatingExpenses: "Operating expenses",
  OtherIncome: "Other income",
  OtherExpenses: "Other expenses",
  // Balance sheet sections.
  CurrentAssets: "Current assets",
  NonCurrentAssets: "Non-current assets",
  CurrentLiabilities: "Current liabilities",
  NonCurrentLiabilities: "Non-current liabilities",
  Equity: "Equity",
};

type Section = NonNullable<NonNullable<StatementGroup["rows"]>[number]["section"]>;
const PL_SECTIONS: Section[] = ["Revenue", "CostOfSales", "OperatingExpenses", "OtherIncome", "OtherExpenses"];

/**
 * The P&L's two groups split into its sections, so it reads Revenue, Cost of sales, Gross profit, Operating expenses,
 * Other income, Other expenses. A row without a section stays with its group's main section.
 */
function profitAndLossSections(groups: StatementGroup[]): StatementGroup[] {
  const rows = groups.flatMap((g) =>
    (g.rows ?? []).map((r) => ({ ...r, section: r.section ?? (g.classification === "revenue" ? "Revenue" : "OperatingExpenses") })),
  );
  return PL_SECTIONS.map((section) => {
    const inSection = rows.filter((r) => r.section === section);
    return {
      classification: section,
      rows: inSection,
      total: inSection.reduce((t, r) => t + num(r.amount), 0),
      platformTotal: inSection.reduce((t, r) => t + num(r.platformAmount), 0),
    };
  }).filter((g) => g.classification === "Revenue" || (g.rows ?? []).length > 0);
}

type BsSection = NonNullable<NonNullable<StatementGroup["rows"]>[number]["balanceSheetSection"]>;
const BS_SECTIONS: BsSection[] = ["CurrentAssets", "NonCurrentAssets", "CurrentLiabilities", "NonCurrentLiabilities", "Equity"];
const BS_DEFAULT: Record<string, BsSection> = { asset: "CurrentAssets", liability: "CurrentLiabilities", equity: "Equity" };

/**
 * The balance sheet's three groups split into current and non-current sections (Mercury's layout). Non-current sections
 * show only when they hold accounts; a row without a section stays with its group's current section.
 */
function balanceSheetSections(groups: StatementGroup[]): StatementGroup[] {
  const rows = groups.flatMap((g) =>
    (g.rows ?? []).map((r) => ({ ...r, balanceSheetSection: r.balanceSheetSection ?? BS_DEFAULT[g.classification ?? ""] ?? "CurrentAssets" })),
  );
  return BS_SECTIONS.map((section) => {
    const inSection = rows.filter((r) => r.balanceSheetSection === section);
    return {
      classification: section,
      rows: inSection,
      total: inSection.reduce((t, r) => t + num(r.amount), 0),
      platformTotal: inSection.reduce((t, r) => t + num(r.platformAmount), 0),
    };
  }).filter((g) => !g.classification.startsWith("NonCurrent") || (g.rows ?? []).length > 0);
}

/** Total assets after the last asset section, Total liabilities after the last liability section. */
const assetsAfter = (groups: StatementGroup[]) => (groups.some((g) => g.classification === "NonCurrentAssets") ? "NonCurrentAssets" : "CurrentAssets");
const liabilitiesAfter = (groups: StatementGroup[]) =>
  groups.some((g) => g.classification === "NonCurrentLiabilities") ? "NonCurrentLiabilities" : "CurrentLiabilities";

/** Gross profit sits after Cost of sales, or straight after Revenue in books with no cost of sales. */
const grossAfter = (groups: StatementGroup[]) => (groups.some((g) => g.classification === "CostOfSales") ? "CostOfSales" : "Revenue");

const naturalOf = (classification?: string | null): DrillTarget["natural"] =>
  ["asset", "expense", "CostOfSales", "OperatingExpenses", "OtherExpenses", "CurrentAssets", "NonCurrentAssets"].includes(classification ?? "") ? "debit" : "credit";

/** Columns for a wide pane, and their one-column stand-ins for a narrow one (the agent open). */
const WIDE = "hidden @2xl:table-cell";
const NARROW = "@2xl:hidden";
/** Narrowest: the platform's figure gives way so the difference itself stays on screen. */
const NARROWER = "hidden @xl:table-cell @2xl:hidden";

const accountName = (r: { code?: string | null; name?: string | null }) => (r.code ? `${r.code} · ${r.name}` : (r.name ?? ""));

/**
 * Report options in the URL beside the period (D49). `date` is a legacy exact as-at override: Setup links the trial
 * balance at the opening-balance date, which no month period expresses.
 */
function useReportOptions(period: Period) {
  const [params, setParams] = useSearchParams();
  const set = (key: string, value: string | null) =>
    setParams(
      (p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: true },
    );
  const override = params.get("date");
  const raw = params.get("view");
  const view: ReportView = raw === "monthly" || raw === "quarterly" ? raw : "total";
  return {
    asAt: override ?? period.asAt,
    override,
    view,
    // The platform column is accrual only: no connector's cash report is verified yet. One figure per account, so the
    // monthly view leaves it out.
    compare: params.get("compare") !== "off" && period.basis === "Accrual" && view === "total",
    basis: period.basis,
    set,
  };
}

type Options = ReturnType<typeof useReportOptions>;

function Filters({
  options,
  asAt,
  monthly,
  closeDate,
  columns,
  platform,
  onExport,
}: {
  options: Options;
  asAt?: string;
  monthly?: boolean;
  closeDate?: string | null;
  /** The monthly or quarterly view's columns, for checking each one's end against the platform. */
  columns?: Array<{ label: string; asAt: string }>;
  platform: Platform;
  /** Downloads the report's every account, whatever the table filters; absent until there is something to export. */
  onExport?: () => void;
}) {
  const p = options;
  const { links } = useBooks();
  const name = platformName(platform);
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {monthly ? <ViewToggle value={p.view} onChange={(v) => p.set("view", v === "total" ? null : v)} /> : null}
        {asAt && p.view === "total" ? (
          <p className="text-sm text-muted-foreground">
            As at <span className="font-semibold text-foreground">{formatCalendarDate(asAt)}</span>
            {p.override ? (
              <>
                {" "}
                from the link.{" "}
                <button type="button" className="font-medium text-foreground underline underline-offset-4" onClick={() => p.set("date", null)}>
                  Use the period instead
                </button>
              </>
            ) : asAt === todayIso() ? null : (
              ", the end of the period"
            )}
          </p>
        ) : null}
        {closeDate ? (
          <p className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            Closed to {formatCalendarDate(closeDate)}
            <InfoHint label="What closed means">The books are locked up to this date, so figures up to it won't change.</InfoHint>
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {platform && !platform.connected ? (
          // The comparison is the reason to connect, so a report without one says how to get it.
          <Link to={links.accountingConnection} className="text-sm font-medium underline underline-offset-4">
            {platform.name ? `Reconnect ${platform.name}` : "Connect Xero or QuickBooks"} to check these books
          </Link>
        ) : p.basis === "Cash" ? (
          <p className="text-sm text-muted-foreground">Comparison with {name} is on accrual only</p>
        ) : p.view !== "total" ? null : (
          <div className="flex items-center gap-2">
            <Switch id="compare" checked={p.compare} onCheckedChange={(on) => p.set("compare", on ? null : "off")} />
            <Label htmlFor="compare">Compare with {name}</Label>
          </div>
        )}
        <ExportButton onExport={() => onExport?.()} disabled={!onExport} />
      </div>
      {platform?.connected && p.basis === "Accrual" && p.view !== "total" && columns?.length ? (
        <MonthChecks columns={columns} name={name} />
      ) : null}
    </div>
  );
}

/**
 * Whether the books agreed with the platform at each column's end: "since when has it differed?" at a glance. Read one
 * column at a time, on request.
 */
// shortcut: a platform trial balance per column, uncached; cache month ends on the backend to run this on load.
function MonthChecks({ columns, name }: { columns: Array<{ label: string; asAt: string }>; name: string }) {
  const { books } = useBooks();
  const withPeriod = usePeriodLink();
  const [results, setResults] = useState<Record<string, number | "failed">>({});
  const [running, setRunning] = useState(false);
  const run = useRef(0);
  const key = columns.map((c) => c.asAt).join(",");
  // New columns drop the old answers and stop a check still running.
  useEffect(() => {
    run.current++;
    setResults({});
    setRunning(false);
  }, [key]);

  const check = async () => {
    const id = ++run.current;
    setResults({});
    setRunning(true);
    for (const c of columns) {
      const { data, error } = await books.GET("/v1/books/current/trial-balance", {
        params: { query: { Date: c.asAt, Compare: "platform", Basis: "Accrual" } },
      });
      if (run.current !== id) return;
      setResults((r) => ({ ...r, [c.asAt]: error || !data?.compared ? "failed" : (data.rows ?? []).filter(differs).length }));
    }
    setRunning(false);
  };

  const started = running || Object.keys(results).length > 0;
  return (
    <div className="flex basis-full flex-wrap items-center gap-1.5 text-sm">
      <span className="mr-1 text-muted-foreground">{name} at each column's end</span>
      {!started ? (
        <Button variant="outline" size="sm" onClick={check}>
          Check against {name}
        </Button>
      ) : (
        columns.map((c) => {
          const r = results[c.asAt];
          return (
            <Link
              key={c.asAt}
              to={withPeriod(`/books/trial-balance?date=${c.asAt}`)}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium tabular-nums",
                r === undefined || r === "failed"
                  ? "bg-muted text-muted-foreground"
                  : r === 0
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-amber-500/15 text-amber-800 dark:text-amber-300",
              )}
            >
              {c.label}
              {r === undefined ? (
                <span className="text-muted-foreground">{running ? "..." : ""}</span>
              ) : r === "failed" ? (
                " couldn't check"
              ) : r === 0 ? (
                <Check className="size-3.5" aria-label="agrees" />
              ) : (
                ` ${r} differ`
              )}
            </Link>
          );
        })
      )}
    </div>
  );
}

function ReportError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return <ErrorState message={apiErrorMessage(error, "Couldn't load the report.")} onRetry={onRetry} />;
}

/** A total-view statement as CSV rows: groups, their totals and the footer, with the platform columns when compared. */
function statementCsv(groups: StatementGroup[], compared: boolean, footer: Array<{ label: string; amount: Amount; platform: Amount }>) {
  const rows: Array<Array<string | number>> = [["Account", "Books", ...(compared ? ["Platform", "Difference"] : [])]];
  for (const g of groups) {
    const label = CLASSIFICATION_LABEL[g.classification ?? ""] ?? g.classification ?? "";
    rows.push([label]);
    for (const r of g.rows ?? []) rows.push([accountName(r), num(r.amount), ...(compared ? [num(r.platformAmount), num(r.difference)] : [])]);
    rows.push([`Total ${label.toLowerCase()}`, num(g.total), ...(compared ? [num(g.platformTotal), num(g.total) - num(g.platformTotal)] : [])]);
  }
  for (const f of footer) rows.push([f.label, num(f.amount), ...(compared ? [num(f.platform), num(f.amount) - num(f.platform)] : [])]);
  return rows;
}

/* ---------- Trial balance ---------- */

export function TrialBalanceReport() {
  return (
    <BooksPeriodScreen
      basis
      title="Trial balance"
      subtitle="Every account's balance at a date. Beside your platform's, any difference is highlighted."
    >
      {(book, period) => <TrialBalanceBody period={period} closeDate={book.closeDate} />}
    </BooksPeriodScreen>
  );
}

function TrialBalanceBody({ period, closeDate }: { period: Period; closeDate?: string | null }) {
  const { books } = useBooks();
  const p = useReportOptions(period);
  const platform = usePlatform();
  const drill = useDrilldown(period, platform);
  const tb = useLoad(
    () =>
      books.GET("/v1/books/current/trial-balance", {
        params: { query: { Date: p.asAt, Compare: p.compare ? "platform" : undefined, Basis: p.basis } },
      }),
    [books, p.asAt, p.compare, p.basis],
  );
  const compared = Boolean(tb.data?.compared);
  const all = tb.data?.rows ?? [];
  const differing = all.filter(differs);
  const differences = differing.length;
  const [only, setOnly] = useOnlyDifferences(`${p.asAt}:${p.basis}:${p.compare}`, differences);
  const rows = compared && only ? differing : all;
  const opened = rows.filter((r) => r.ledgerAccountId);
  const targets: DrillTarget[] = opened.map((r) => {
    const natural = naturalOf(r.classification);
    const balance = num(r.debit) - num(r.credit);
    const sign = natural === "credit" ? -1 : 1;
    // An income or expense balance is the financial year to date, so its lines start with the year (or the books).
    const yearStart = `${financialYearStart(monthOf(p.asAt), period.fyStart)}-01`;
    const plAccount = r.classification === "revenue" || r.classification === "expense";
    return {
      accountId: r.ledgerAccountId!,
      label: accountName(r),
      from: plAccount && yearStart > `${period.months[0] ?? ""}-01` ? yearStart : undefined,
      to: p.asAt,
      amount: sign * balance,
      natural,
      caption: `Balance at ${formatCalendarDate(p.asAt)}`,
      platform: compared ? { amount: sign * (num(r.platformDebit) - num(r.platformCredit)), difference: sign * num(r.difference) } : undefined,
    };
  });

  return (
    <>
      <Filters
        options={p}
        platform={platform}
        asAt={p.asAt}
        closeDate={closeDate}
        onExport={
          tb.data
            ? () =>
                downloadCsv(csvName("trial-balance", period, p.asAt), [
                  ["Account", "Debit", "Credit", ...(compared ? ["Platform debit", "Platform credit", "Difference"] : [])],
                  ...all.map((r) => [
                    accountName(r),
                    num(r.debit),
                    num(r.credit),
                    ...(compared ? [num(r.platformDebit), num(r.platformCredit), num(r.difference)] : []),
                  ]),
                  ["Total", num(tb.data!.totalDebit), num(tb.data!.totalCredit), ...(compared
                    ? [
                        num(tb.data!.platformTotalDebit),
                        num(tb.data!.platformTotalCredit),
                        num(tb.data!.totalDebit) - num(tb.data!.totalCredit) - (num(tb.data!.platformTotalDebit) - num(tb.data!.platformTotalCredit)),
                      ]
                    : [])],
                ])
            : undefined
        }
      />
      {tb.loading && !tb.data ? (
        <LoadingState label={p.compare ? "Loading the trial balance and your platform's" : "Loading the trial balance"} />
      ) : tb.error ? (
        <ReportError error={tb.error} onRetry={tb.reload} />
      ) : !tb.data || rows.length === 0 ? (
        <EmptyState icon={FileText} title="Nothing in the books at this date" />
      ) : (
        <>
          {compared ? <CompareSummary differences={differences} name={platformName(platform)} when={`at ${formatCalendarDate(p.asAt)}`} only={only} onOnly={setOnly} /> : null}
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Account</TableHead>
                  {/* In a narrow pane (the agent open) each side is one Dr/Cr column, so the difference stays in view. */}
                  <TableHead className={cn(TH, "text-right", WIDE)}>Debit</TableHead>
                  <TableHead className={cn(TH, "text-right", WIDE)}>Credit</TableHead>
                  <TableHead className={cn(TH, "text-right", NARROW)}>Balance</TableHead>
                  {compared ? (
                    <>
                      <TableHead className={cn(TH, "text-right", WIDE)}>Platform debit</TableHead>
                      <TableHead className={cn(TH, "text-right", WIDE)}>Platform credit</TableHead>
                      <TableHead className={cn(TH, "text-right", NARROWER)}>Platform</TableHead>
                      <TableHead className={cn(TH, "text-right")}>Difference</TableHead>
                    </>
                  ) : null}
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => {
                  const at = r.ledgerAccountId ? opened.indexOf(r) : -1;
                  return (
                    <TableRow
                      key={r.ledgerAccountId ?? `row-${i}`}
                      {...rowLink(at >= 0 ? () => drill.open(targets, at) : undefined)}
                      data-state={at >= 0 && drill.openKey === drillKey(targets[at]!) ? "selected" : undefined}
                    >
                      <TableCell className="font-medium">{accountName(r)}</TableCell>
                      <TableCell className={cn("text-right tabular-nums", WIDE)}>{moneyOrBlank(r.debit)}</TableCell>
                      <TableCell className={cn("text-right tabular-nums", WIDE)}>{moneyOrBlank(r.credit)}</TableCell>
                      <TableCell className={cn("whitespace-nowrap text-right tabular-nums", NARROW)}>{drCr(r.debit, r.credit)}</TableCell>
                      {compared ? (
                        <>
                          <TableCell className={cn("text-right tabular-nums text-muted-foreground", WIDE)}>{moneyOrBlank(r.platformDebit)}</TableCell>
                          <TableCell className={cn("text-right tabular-nums text-muted-foreground", WIDE)}>{moneyOrBlank(r.platformCredit)}</TableCell>
                          <TableCell className={cn("whitespace-nowrap text-right tabular-nums text-muted-foreground", NARROWER)}>
                            {drCr(r.platformDebit, r.platformCredit)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DifferenceCell value={r.difference} compared />
                          </TableCell>
                        </>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className={cn("text-right font-semibold tabular-nums", WIDE)}>{money(tb.data.totalDebit)}</TableCell>
                  <TableCell className={cn("text-right font-semibold tabular-nums", WIDE)}>{money(tb.data.totalCredit)}</TableCell>
                  <TableCell className={cn("text-right text-xs text-muted-foreground", NARROW)}>
                    {num(tb.data.totalDebit) === num(tb.data.totalCredit) ? "Balances" : `Off by ${money(num(tb.data.totalDebit) - num(tb.data.totalCredit))}`}
                  </TableCell>
                  {compared ? (
                    <>
                      <TableCell className={cn("text-right font-semibold tabular-nums", WIDE)}>{money(tb.data.platformTotalDebit)}</TableCell>
                      <TableCell className={cn("text-right font-semibold tabular-nums", WIDE)}>{money(tb.data.platformTotalCredit)}</TableCell>
                      <TableCell className={NARROWER} />
                      <TableCell />
                    </>
                  ) : null}
                </TableRow>
              </TableFooter>
            </Table>
          </TableShell>
        </>
      )}
      {drill.sheet}
    </>
  );
}

/** The report's one line on the platform: agrees, or how many accounts differ, with a switch to show only those. */
function CompareSummary({
  differences,
  name,
  when,
  only,
  onOnly,
}: {
  differences: number;
  name: string;
  /** "at 31 Oct 2026" or "for Apr - Oct 2026". */
  when: string;
  only: boolean;
  onOnly: (only: boolean) => void;
}) {
  return differences === 0 ? (
    <p className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-1 text-sm font-medium text-emerald-700 dark:text-emerald-400">
      <Check className="size-4" /> Agrees with {name} {when}
    </p>
  ) : (
    <div className="mb-3 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <p className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-1 text-sm font-medium text-amber-800 dark:text-amber-300">
          <TriangleAlert className="size-4" /> {differences} account{differences === 1 ? "" : "s"} differ from {name} {when}
        </p>
        <Button variant="ghost" size="sm" onClick={() => onOnly(!only)}>
          {only ? "Show every account" : `Show only ${differences === 1 ? "that one" : "those"}`}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        A difference is activity in {name} that is not in these books, such as journals entered there directly.
      </p>
    </div>
  );
}

const differs = (r: { difference?: Amount }) => num(r.difference) !== 0;

/**
 * "Show only those", switched off whenever the view (date, period, basis, comparison) changes or there is nothing left to
 * filter, so it never comes back on by itself.
 */
function useOnlyDifferences(view: string, differences: number) {
  const [only, setOnly] = useState(false);
  const [seen, setSeen] = useState(view);
  // Reset during render, so the new view never draws filtered.
  if (seen !== view || (only && differences === 0)) {
    setSeen(view);
    setOnly(false);
  }
  return [only, setOnly] as const;
}

/* ---------- Profit and loss ---------- */

export function ProfitAndLossReport() {
  return (
    <BooksPeriodScreen
      basis
      title="Profit and loss"
      subtitle="Revenue less cost of sales gives gross profit; less expenses, net profit. On cash basis, invoices and bills count when they are paid."
    >
      {(book, period) => <ProfitAndLossBody period={period} closeDate={book.closeDate} />}
    </BooksPeriodScreen>
  );
}

function ProfitAndLossBody({ period, closeDate }: { period: Period; closeDate?: string | null }) {
  const { books } = useBooks();
  const p = useReportOptions(period);
  const platform = usePlatform();
  const drill = useDrilldown(period, platform);
  const monthly = p.view !== "total";
  const cols = monthly ? buckets(period, p.view as "monthly" | "quarterly") : [];
  const pl = useLoad(
    () =>
      books.GET("/v1/books/current/profit-and-loss", {
        params: { query: { From: period.fromDate, To: period.toDate, Compare: p.compare ? "platform" : undefined, Basis: p.basis } },
      }),
    [books, period.fromDate, period.toDate, p.compare, p.basis],
    { enabled: !monthly },
  );
  const byMonth = useLoad(
    () =>
      readAll<ProfitAndLoss>(
        cols.map((c) => books.GET("/v1/books/current/profit-and-loss", { params: { query: { From: c.from, To: c.to, Basis: p.basis } } })),
      ),
    [books, cols.map((c) => `${c.from}:${c.to}`).join(","), p.basis],
    { enabled: monthly && cols.length <= MAX_MONTHS },
  );
  const data = pl.data;
  const caption = periodLabel(period.from, period.to);

  return (
    <>
      <ProfitAndLossChart period={period} />
      <Filters
        options={p}
        platform={platform}
        monthly
        closeDate={closeDate}
        columns={cols}
        onExport={
          monthly
            ? byMonth.data?.length === cols.length
              ? () =>
                  downloadCsv(
                    csvName(`profit-and-loss-${p.view}`, period),
                    monthlyCsv(
                      cols,
                      byMonth.data!.map((r) => profitAndLossSections(r.groups ?? [])),
                      [
                        { label: "Gross profit", values: byMonth.data!.map((r) => num(r.grossProfit)) },
                        { label: "Net profit", values: byMonth.data!.map((r) => num(r.netProfit)) },
                      ],
                      true,
                    ),
                  )
              : undefined
            : data
              ? () =>
                  downloadCsv(
                    csvName("profit-and-loss", period),
                    statementCsv(profitAndLossSections(data.groups ?? []), Boolean(data.compared), [
                      { label: "Gross profit", amount: data.grossProfit, platform: data.platformGrossProfit },
                      { label: "Net profit", amount: data.netProfit, platform: data.platformNetProfit },
                    ]),
                  )
              : undefined
        }
      />
      {monthly ? (
        cols.length > MAX_MONTHS ? (
          <EmptyState icon={FileText} title={`Pick ${MAX_MONTHS} months or fewer`}>The monthly view has a column per month.</EmptyState>
        ) : byMonth.loading && !byMonth.data ? (
          <LoadingState label={`Loading the profit and loss ${p.view === "quarterly" ? "quarter" : "month"} by ${p.view === "quarterly" ? "quarter" : "month"}`} />
        ) : byMonth.error ? (
          <ReportError error={byMonth.error} onRetry={byMonth.reload} />
        ) : !byMonth.data || byMonth.data.length !== cols.length ? (
          // Switching Monthly to Quarterly redraws before the old answer is dropped; never draw new columns on it.
          <LoadingState label="Loading the profit and loss" />
        ) : (
          <MonthlyStatement
            columns={cols}
            results={byMonth.data.map((r) => profitAndLossSections(r.groups ?? []))}
            withTotal={{ from: period.fromDate, to: period.toDate, caption }}
            subtotals={[{ after: grossAfter(byMonth.data.flatMap((r) => profitAndLossSections(r.groups ?? []))), label: "Gross profit", values: byMonth.data.map((r) => num(r.grossProfit)) }]}
            footer={[{ label: "Net profit", values: byMonth.data.map((r) => num(r.netProfit)) }]}
            drill={drill}
          />
        )
      ) : pl.loading && !data ? (
        <LoadingState label="Loading the profit and loss" />
      ) : pl.error ? (
        <ReportError error={pl.error} onRetry={pl.reload} />
      ) : !data ? null : (
        <StatementTable
          view={`${caption}:${p.basis}:${p.compare}`}
          groups={profitAndLossSections(data.groups ?? [])}
          compared={Boolean(data.compared)}
          platform={platform}
          when={`for ${caption}`}
          subtotals={[{ after: grossAfter(profitAndLossSections(data.groups ?? [])), label: "Gross profit", amount: data.grossProfit, platform: data.platformGrossProfit }]}
          footer={[{ label: "Net profit", amount: data.netProfit, platform: data.platformNetProfit }]}
          target={(r, g) => ({ from: period.fromDate, to: period.toDate, caption, natural: naturalOf(g.classification), amount: r.amount })}
          drill={drill}
        />
      )}
      {drill.sheet}
    </>
  );
}

/** Income and expenses for each month of the period, over the report. */
function ProfitAndLossChart({ period }: { period: Period }) {
  const summary = useMonthlySummary(period.basis);
  const months = monthsBetween(period.from, period.to);
  if (months.length < 2 || !summary.data) return null;
  const byMonth = new Map((summary.data.months ?? []).map((m) => [(m.month ?? "").slice(0, 7), m]));
  const net = months.reduce((s, m) => s + num(byMonth.get(m)?.net), 0);
  return (
    <div className="mb-5">
      <MonthBars
        labels={months.map((m) => monthLabel(m))}
        series={[
          { label: "Income", className: "bg-emerald-500/70", values: months.map((m) => num(byMonth.get(m)?.income)) },
          { label: "Expenses", className: "bg-rose-500/60", values: months.map((m) => num(byMonth.get(m)?.expenses)) },
          { label: "Net", className: "bg-foreground/50", values: months.map((m) => num(byMonth.get(m)?.net)) },
        ]}
        summary={`Net profit ${money(net)}`}
        openLast={period.asAt < period.toDate}
      />
    </div>
  );
}

/* ---------- Balance sheet ---------- */

export function BalanceSheetReport() {
  return (
    <BooksPeriodScreen
      basis
      title="Balance sheet"
      subtitle="Equity includes the current year's earnings. On cash basis, unpaid invoices and bills are left out."
    >
      {(book, period) => <BalanceSheetBody period={period} closeDate={book.closeDate} />}
    </BooksPeriodScreen>
  );
}

function BalanceSheetBody({ period, closeDate }: { period: Period; closeDate?: string | null }) {
  const { books } = useBooks();
  const p = useReportOptions(period);
  const platform = usePlatform();
  const drill = useDrilldown(period, platform);
  const monthly = p.view !== "total";
  const cols = monthly ? buckets(period, p.view as "monthly" | "quarterly") : [];
  const ends = cols.map((c) => c.asAt);
  const bs = useLoad(
    () =>
      books.GET("/v1/books/current/balance-sheet", {
        params: { query: { Date: p.asAt, Compare: p.compare ? "platform" : undefined, Basis: p.basis } },
      }),
    [books, p.asAt, p.compare, p.basis],
    { enabled: !monthly },
  );
  const byMonth = useLoad(
    () => readAll<BalanceSheet>(ends.map((d) => books.GET("/v1/books/current/balance-sheet", { params: { query: { Date: d, Basis: p.basis } } }))),
    [books, ends.join(","), p.basis],
    { enabled: monthly && cols.length <= MAX_MONTHS },
  );
  const data = bs.data;
  return (
    <>
      <Filters
        options={p}
        platform={platform}
        asAt={p.asAt}
        monthly
        closeDate={closeDate}
        columns={cols}
        onExport={
          monthly
            ? byMonth.data?.length === cols.length
              ? () =>
                  downloadCsv(
                    csvName(`balance-sheet-${p.view}`, period),
                    monthlyCsv(
                      cols,
                      byMonth.data!.map((r) => balanceSheetSections(r.groups ?? [])),
                      [
                        { label: "Total assets", values: byMonth.data!.map((r) => num(r.totalAssets)) },
                        { label: "Total liabilities", values: byMonth.data!.map((r) => total(r, "liability")) },
                        { label: "Total liabilities and equity", values: byMonth.data!.map((r) => num(r.totalLiabilitiesAndEquity)) },
                        { label: "Working capital", values: byMonth.data!.map((r) => num(r.workingCapital)) },
                      ],
                      false,
                    ),
                  )
              : undefined
            : data
              ? () =>
                  downloadCsv(
                    csvName("balance-sheet", period, p.asAt),
                    statementCsv(balanceSheetSections(data.groups ?? []), Boolean(data.compared), [
                      { label: "Total assets", amount: data.totalAssets, platform: data.platformTotalAssets },
                      { label: "Total liabilities", amount: total(data, "liability"), platform: platformTotal(data, "liability") },
                      { label: "Total liabilities and equity", amount: data.totalLiabilitiesAndEquity, platform: data.platformTotalLiabilitiesAndEquity },
                      { label: "Working capital", amount: data.workingCapital, platform: data.platformWorkingCapital },
                    ]),
                  )
              : undefined
        }
      />
      {monthly ? (
        cols.length > MAX_MONTHS ? (
          <EmptyState icon={FileText} title={`Pick ${MAX_MONTHS} months or fewer`}>The monthly view has a column per month.</EmptyState>
        ) : byMonth.loading && !byMonth.data ? (
          <LoadingState label="Loading the balance sheets" />
        ) : byMonth.error ? (
          <ReportError error={byMonth.error} onRetry={byMonth.reload} />
        ) : !byMonth.data || byMonth.data.length !== cols.length ? (
          <LoadingState label="Loading the balance sheets" />
        ) : (
          <>
            {cols.length > 1 ? (
              <div className="mb-5">
                <MonthBars
                  labels={cols.map((c) => c.label)}
                  series={[
                    { label: "Assets", className: "bg-accent-blue/60", values: byMonth.data.map((r) => num(r.totalAssets)) },
                    { label: "Liabilities", className: "bg-rose-500/50", values: byMonth.data.map((r) => total(r, "liability")) },
                    { label: "Equity", className: "bg-emerald-500/60", values: byMonth.data.map((r) => total(r, "equity")) },
                  ]}
                  summary={`At ${formatCalendarDate(ends[ends.length - 1]!)}`}
                  openLast={period.asAt < period.toDate}
                />
              </div>
            ) : null}
            <MonthlyStatement
              columns={cols.map((c) => ({ label: c.label, to: c.asAt, caption: `Balance at ${formatCalendarDate(c.asAt)}` }))}
              results={byMonth.data.map((r) => balanceSheetSections(r.groups ?? []))}
              subtotals={[
                { after: assetsAfter(byMonth.data.flatMap((r) => balanceSheetSections(r.groups ?? []))), label: "Total assets", values: byMonth.data.map((r) => num(r.totalAssets)) },
                { after: liabilitiesAfter(byMonth.data.flatMap((r) => balanceSheetSections(r.groups ?? []))), label: "Total liabilities", values: byMonth.data.map((r) => total(r, "liability")) },
              ]}
              footer={[
                { label: "Total liabilities and equity", values: byMonth.data.map((r) => num(r.totalLiabilitiesAndEquity)) },
                { label: "Working capital", values: byMonth.data.map((r) => num(r.workingCapital)) },
              ]}
              drill={drill}
            />
          </>
        )
      ) : bs.loading && !data ? (
        <LoadingState label="Loading the balance sheet" />
      ) : bs.error ? (
        <ReportError error={bs.error} onRetry={bs.reload} />
      ) : !data ? null : (
        <>
          <p
            className={cn(
              "mb-3 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium",
              data.balanced
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "bg-destructive/10 text-destructive",
            )}
          >
            {data.balanced ? <Check className="size-4" /> : <TriangleAlert className="size-4" />}
            {data.balanced ? "Balances" : "Does not balance"}: assets {money(data.totalAssets)}, liabilities and equity{" "}
            {money(data.totalLiabilitiesAndEquity)}
          </p>
          <StatementTable
            view={`${p.asAt}:${p.basis}:${p.compare}`}
            groups={balanceSheetSections(data.groups ?? [])}
            compared={Boolean(data.compared)}
            platform={platform}
            when={`at ${formatCalendarDate(p.asAt)}`}
            target={(r, g) => ({ to: p.asAt, caption: `Balance at ${formatCalendarDate(p.asAt)}`, natural: naturalOf(g.classification), amount: r.amount })}
            subtotals={[
              { after: assetsAfter(balanceSheetSections(data.groups ?? [])), label: "Total assets", amount: data.totalAssets, platform: data.platformTotalAssets },
              {
                after: liabilitiesAfter(balanceSheetSections(data.groups ?? [])),
                label: "Total liabilities",
                amount: total(data, "liability"),
                platform: platformTotal(data, "liability"),
              },
            ]}
            footer={[
              {
                label: "Total liabilities and equity",
                amount: data.totalLiabilitiesAndEquity,
                platform: data.platformTotalLiabilitiesAndEquity,
              },
              { label: "Working capital", amount: data.workingCapital, platform: data.platformWorkingCapital },
            ]}
            drill={drill}
          />
        </>
      )}
      {drill.sheet}
    </>
  );
}

const total = (r: BalanceSheet, classification: string) => num(r.groups?.find((g) => g.classification === classification)?.total);
const platformTotal = (r: BalanceSheet, classification: string) => r.groups?.find((g) => g.classification === classification)?.platformTotal;

/* ---------- Statement tables ---------- */

type Amount = number | string | null | undefined;
type Row = NonNullable<StatementGroup["rows"]>[number];
type Drill = ReturnType<typeof useDrilldown>;

function StatementTable({
  groups: all,
  compared,
  platform,
  view,
  when,
  footer,
  subtotals = [],
  target,
  drill,
}: {
  groups: StatementGroup[];
  compared: boolean;
  platform: Platform;
  /** What the table is showing, so "Show only those" lets go when it changes. */
  view: string;
  /** When the comparison holds: "at 31 Oct 2026" or "for Apr - Oct 2026". */
  when: string;
  footer: Array<{ label: string; amount: Amount; platform: Amount }>;
  /** Lines between groups, such as Gross profit after Cost of sales. */
  subtotals?: Array<{ after: string; label: string; amount: Amount; platform: Amount }>;
  /** What a row opens in the drill-down, beside its account. */
  target: (row: Row, group: StatementGroup) => Omit<DrillTarget, "accountId" | "label">;
  drill: Drill;
}) {
  const differences = all.flatMap((g) => g.rows ?? []).filter(differs).length;
  const [only, setOnly] = useOnlyDifferences(view, differences);
  if (all.every((g) => (g.rows ?? []).length === 0)) {
    return <EmptyState icon={FileText} title="Nothing in the books for this period" />;
  }
  const cols = compared ? 4 : 2;
  // Only the differing accounts; every section and total stays, so subtotals placed after a section still show.
  const groups = compared && only ? all.map((g) => ({ ...g, rows: (g.rows ?? []).filter(differs) })) : all;
  // Every account row in report order, so the panel steps through the report.
  const opened = groups.flatMap((g) => (g.rows ?? []).filter((r) => r.ledgerAccountId).map((r) => ({ r, g })));
  const targets: DrillTarget[] = opened.map(({ r, g }) => ({
    accountId: r.ledgerAccountId!,
    label: accountName(r),
    ...target(r, g),
    platform: compared ? { amount: num(r.platformAmount), difference: num(r.difference) } : undefined,
  }));
  return (
    <>
      {compared ? <CompareSummary differences={differences} name={platformName(platform)} when={when} only={only} onOnly={setOnly} /> : null}
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>Account</TableHead>
              <TableHead className={cn(TH, "text-right")}>Books</TableHead>
              {compared ? (
                <>
                  <TableHead className={cn(TH, "text-right", "hidden @xl:table-cell")}>Platform</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Difference</TableHead>
                </>
              ) : null}
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => (
              <Fragment key={g.classification}>
                <GroupRows
                  group={g}
                  compared={compared}
                  cols={cols}
                  selected={drill.openKey}
                  indexOf={(r) => opened.findIndex((o) => o.r === r)}
                  targets={targets}
                  onOpen={(i) => drill.open(targets, i)}
                />
                {subtotals.filter((subtotal) => subtotal.after === g.classification).map((subtotal) => (
                  <TableRow key={subtotal.label} className="border-y-2 bg-muted/30 hover:bg-muted/30">
                    <TableCell className="font-semibold">{subtotal.label}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{money(subtotal.amount)}</TableCell>
                    {compared ? (
                      <>
                        <TableCell className="hidden text-right font-semibold tabular-nums text-muted-foreground @xl:table-cell">{money(subtotal.platform)}</TableCell>
                        <TableCell className="text-right">
                          <DifferenceCell value={num(subtotal.amount) - num(subtotal.platform)} compared />
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
          <TableFooter>
            {footer.map((f) => (
              <TableRow key={f.label}>
                <TableCell className="font-semibold">{f.label}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{money(f.amount)}</TableCell>
                {compared ? (
                  <>
                    <TableCell className="hidden text-right font-semibold tabular-nums @xl:table-cell">{money(f.platform)}</TableCell>
                    <TableCell className="text-right">
                      <DifferenceCell value={num(f.amount) - num(f.platform)} compared />
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            ))}
          </TableFooter>
        </Table>
      </TableShell>
    </>
  );
}

function GroupRows({
  group,
  compared,
  cols,
  selected,
  indexOf,
  targets,
  onOpen,
}: {
  group: StatementGroup;
  compared: boolean;
  cols: number;
  selected: string | null;
  indexOf: (row: Row) => number;
  targets: DrillTarget[];
  onOpen: (index: number) => void;
}) {
  return (
    <>
      <TableRow className="bg-muted/20 hover:bg-muted/20">
        <TableCell colSpan={cols} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {CLASSIFICATION_LABEL[group.classification ?? ""] ?? group.classification}
        </TableCell>
      </TableRow>
      {(group.rows ?? []).map((r, i) => {
        const at = r.ledgerAccountId ? indexOf(r) : -1;
        return (
          <TableRow
            key={r.ledgerAccountId ?? `${group.classification}-${i}`}
            {...rowLink(at >= 0 ? () => onOpen(at) : undefined)}
            data-state={at >= 0 && selected === drillKey(targets[at]!) ? "selected" : undefined}
          >
            <TableCell className="pl-6">
              <span className="inline-flex items-center gap-1.5">
                {accountName(r)}
                {/* A P&L figure reads positive on its own side; below zero is worth a look (refunds posted as costs, a fee account netted). */}
                {PL_SECTIONS.includes(group.classification as Section) && num(r.amount) < 0 ? (
                  <InfoHint label={`Why ${r.name} is flagged`} className="text-amber-600 dark:text-amber-400">
                    {naturalOf(group.classification) === "debit"
                      ? "A cost account below zero: more was credited to it than spent. Check for refunds or receipts posted here."
                      : "An income account below zero: more was taken off it than earned. Check for credit notes or refunds posted here."}
                  </InfoHint>
                ) : null}
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">{money(r.amount)}</TableCell>
            {compared ? (
              <>
                <TableCell className="hidden text-right tabular-nums text-muted-foreground @xl:table-cell">{money(r.platformAmount)}</TableCell>
                <TableCell className="text-right">
                  <DifferenceCell value={r.difference} compared />
                </TableCell>
              </>
            ) : null}
          </TableRow>
        );
      })}
      <TableRow className="hover:bg-transparent">
        <TableCell className="pl-6 text-sm font-semibold">
          Total {(CLASSIFICATION_LABEL[group.classification ?? ""] ?? group.classification ?? "").toLowerCase()}
        </TableCell>
        <TableCell className="text-right font-semibold tabular-nums">{money(group.total)}</TableCell>
        {compared ? (
          <>
            <TableCell className="hidden text-right font-semibold tabular-nums text-muted-foreground @xl:table-cell">{money(group.platformTotal)}</TableCell>
            <TableCell className="text-right">
              <DifferenceCell value={num(group.total) - num(group.platformTotal)} compared />
            </TableCell>
          </>
        ) : null}
      </TableRow>
    </>
  );
}

interface MonthColumn {
  label: string;
  /** First day for a flow (P&L); omitted for a balance. */
  from?: string;
  to: string;
  caption: string;
}

/** The server's row order (code, accounts without one last, then name), so Monthly lists accounts as Total does. */
const byCode = (a: Row, b: Row) =>
  Number(a.code == null) - Number(b.code == null) || (a.code ?? "").localeCompare(b.code ?? "") || (a.name ?? "").localeCompare(b.name ?? "");

/**
 * Month results as one grid: per classification, the union of accounts across the months, each with a figure per
 * month and its total.
 */
function monthlySections(results: StatementGroup[][], months: number) {
  // Every month lists its non-empty sections, so sections follow the statement's own order, not first appearance.
  const order = [...PL_SECTIONS, ...BS_SECTIONS, "revenue", "expense", "asset", "liability", "equity"] as string[];
  const classifications = [...new Set(results.flatMap((groups) => groups.map((g) => g.classification ?? "")))].sort(
    (a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99),
  );
  // A computed row (Current year earnings) has no account; its name keys it instead.
  const keyOf = (r: Row) => r.ledgerAccountId ?? `name:${r.name}`;
  return classifications.map((c) => {
    const group = (col: number) => results[col]?.find((g) => g.classification === c);
    const accounts = new Map<string, Row>();
    for (let col = 0; col < months; col++) for (const r of group(col)?.rows ?? []) if (!accounts.has(keyOf(r))) accounts.set(keyOf(r), r);
    const rows = [...accounts.values()].sort(byCode).map((r) => {
      const values = Array.from({ length: months }, (_, col) => num(group(col)?.rows?.find((x) => keyOf(x) === keyOf(r))?.amount));
      return { key: keyOf(r), row: r, values, total: values.reduce((a, b) => a + b, 0) };
    });
    const totals = Array.from({ length: months }, (_, col) => num(group(col)?.total));
    return { classification: c, rows, totals };
  });
}

/** The monthly grid as CSV rows: what the table shows, Total column included. */
function monthlyCsv(columns: MonthColumn[], results: StatementGroup[][], footer: Array<{ label: string; values: number[] }>, withTotal: boolean) {
  const add = (xs: number[]) => (withTotal ? [...xs, xs.reduce((a, b) => a + b, 0)] : xs);
  const rows: Array<Array<string | number>> = [["Account", ...columns.map((c) => c.label), ...(withTotal ? ["Total"] : [])]];
  for (const s of monthlySections(results, columns.length)) {
    rows.push([CLASSIFICATION_LABEL[s.classification] ?? s.classification]);
    for (const r of s.rows) rows.push([accountName(r.row), ...add(r.values)]);
    rows.push([`Total ${(CLASSIFICATION_LABEL[s.classification] ?? s.classification).toLowerCase()}`, ...add(s.totals)]);
  }
  for (const f of footer) rows.push([f.label, ...add(f.values)]);
  return rows;
}

/**
 * A statement with a column per month (Mercury's monthly view). The last column (Total, or the latest month) stays
 * pinned on the right and the grid opens scrolled to the latest months. Every figure opens its drill-down, and the
 * panel steps along the row.
 */
function MonthlyStatement({
  columns,
  results,
  withTotal,
  subtotals = [],
  footer,
  drill,
}: {
  columns: MonthColumn[];
  results: StatementGroup[][];
  /** A Total column for a flow statement: the whole period. */
  withTotal?: Omit<MonthColumn, "label">;
  /** Lines between sections, such as Gross profit after Cost of sales. */
  subtotals?: Array<{ after: string; label: string; values: number[] }>;
  footer: Array<{ label: string; values: number[] }>;
  drill: Drill;
}) {
  const all = monthlySections(results, columns.length);
  // Accounts with nothing in any column are hidden by default; Mercury lists every £0.00 row.
  const [showEmpty, setShowEmpty] = useState(false);
  const isEmpty = (r: { values: number[] }) => r.values.every((v) => v === 0);
  const empties = all.reduce((n, s) => n + s.rows.filter(isEmpty).length, 0);
  const sections = showEmpty ? all : all.map((s) => ({ ...s, rows: s.rows.filter((r) => !isEmpty(r)) }));
  // Keyed by the columns, not the results array (a new one each render), so a click doesn't scroll the grid back.
  const scroller = useScrollToEnd<HTMLDivElement>(columns.map((c) => c.label).join("|"));
  // Mercury's collapse: section totals only, for reading the shape of a long period.
  const [collapsed, setCollapsed] = useState(false);
  if (all.every((s) => s.rows.length === 0)) return <EmptyState icon={FileText} title="Nothing in the books for this period" />;

  const allColumns = withTotal ? [...columns, { ...withTotal, label: "Total" }] : columns;
  const last = allColumns.length - 1;
  const add = (xs: number[]) => (withTotal ? [...xs, xs.reduce((a, b) => a + b, 0)] : xs);
  // Only rows with an account open a panel, so previous/next never lands on a computed row.
  const targets: DrillTarget[] = sections.flatMap((s) =>
    s.rows.filter(({ row }) => row.ledgerAccountId).flatMap(({ row, values, total }) =>
      allColumns.map((c, i) => ({
        accountId: row.ledgerAccountId!,
        label: accountName(row),
        from: c.from,
        to: c.to,
        caption: c.caption,
        natural: naturalOf(s.classification),
        amount: i < columns.length ? values[i] : total,
      })),
    ),
  );
  let index = 0;
  const sticky = STICKY_CELL;
  // The pinned last column; `solid` for header, section and footer rows.
  const pin = (ci: number, solid = false) => ci === last && cn(PINNED_CELL, solid && "bg-muted");

  return (
    <div ref={scroller} className="space-y-2">
      <div className="flex justify-end gap-1">
        {empties > 0 && !collapsed ? (
          <Button variant="ghost" size="sm" onClick={() => setShowEmpty((s) => !s)}>
            {showEmpty ? "Hide empty accounts" : `Show ${empties} empty account${empties === 1 ? "" : "s"}`}
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronsUpDown className="size-4" /> : <ChevronsDownUp className="size-4" />}
          {collapsed ? "Show accounts" : "Totals only"}
        </Button>
      </div>
      <TableShell className={MONTH_TABLE}>
        <Table>
          <TableHeader>
            <TableRow className={SOLID_ROW}>
              <TableHead className={cn(TH, sticky, "min-w-48 bg-muted")}>Account</TableHead>
              {allColumns.map((c, ci) => (
                <TableHead key={c.label} className={cn(TH, "min-w-28 text-right", pin(ci, true))}>
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sections.map((s) => (
              <Fragment key={s.classification}>
                <TableRow className={SOLID_ROW}>
                  <TableCell className={cn(sticky, "bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground")}>
                    {CLASSIFICATION_LABEL[s.classification] ?? s.classification}
                  </TableCell>
                  {last > 0 ? <TableCell colSpan={last} /> : null}
                  <TableCell className={cn(pin(last, true))} />
                </TableRow>
                {(collapsed ? [] : s.rows).map(({ key, row, values, total }) => (
                  <TableRow key={key} className="hover:bg-transparent">
                    <TableCell className={cn(sticky, "max-w-64 truncate pl-6")}>{accountName(row)}</TableCell>
                    {[...values, ...(withTotal ? [total] : [])].map((v, ci) => {
                      if (!row.ledgerAccountId)
                        return (
                          <TableCell key={ci} className={cn("px-3 text-right tabular-nums text-muted-foreground", pin(ci))}>
                            {money(v)}
                          </TableCell>
                        );
                      const at = index++;
                      return (
                        <TableCell key={ci} className={cn("p-0", pin(ci))}>
                          <button
                            type="button"
                            onClick={() => drill.open(targets, at)}
                            className={cn(
                              "w-full px-3 py-2 text-right tabular-nums transition hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                              v === 0 && "text-muted-foreground/60",
                              ci === last && "font-medium",
                              drill.openKey === drillKey(targets[at]!) && "bg-accent-blue/10",
                            )}
                          >
                            {money(v)}
                          </button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                <TableRow className="hover:bg-transparent">
                  <TableCell className={cn(sticky, "pl-6 text-sm font-semibold")}>
                    Total {(CLASSIFICATION_LABEL[s.classification] ?? s.classification).toLowerCase()}
                  </TableCell>
                  {add(s.totals).map((v, ci) => (
                    <TableCell key={ci} className={cn("px-3 text-right font-semibold tabular-nums", pin(ci))}>
                      {money(v)}
                    </TableCell>
                  ))}
                </TableRow>
                {subtotals.filter((subtotal) => subtotal.after === s.classification).map((subtotal) => (
                  <TableRow key={subtotal.label} className={SOLID_ROW}>
                    <TableCell className={cn(sticky, "bg-muted font-semibold")}>{subtotal.label}</TableCell>
                    {add(subtotal.values).map((v, ci) => (
                      <TableCell key={ci} className={cn("px-3 text-right font-semibold tabular-nums", pin(ci, true), v < 0 && "text-rose-700 dark:text-rose-400")}>
                        {money(v)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
          <TableFooter>
            {footer.map((f) => (
              <TableRow key={f.label} className={SOLID_ROW}>
                <TableCell className={cn(sticky, "bg-muted font-semibold")}>{f.label}</TableCell>
                {add(f.values).map((v, ci) => (
                  <TableCell key={ci} className={cn("px-3 text-right font-semibold tabular-nums", pin(ci, true), v < 0 && "text-rose-700 dark:text-rose-400")}>
                    {money(v)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        </Table>
      </TableShell>
    </div>
  );
}
